import { z } from "zod";

import { MetaDataSchema } from "@/schemas/meta-data";

import {
	NotificationCategory,
	NotificationEntityType,
	NotificationFrequency,
	NotificationPreferenceGroup,
	NotificationPriority,
	NotificationStatus,
	UserNotificationContentType,
	UserNotificationType,
} from "../enum/notification.enum";

// ── Notification ──────────────────────────────────────────────────────

export const NotificationSchema = z.object({
	id: z.number(),
	type: z.nativeEnum(UserNotificationType),
	contentType: z.string(),
	title: z.string().nullable(),
	message: z.string().nullable(),
	link: z.string().nullable(),
	entityType: z.nativeEnum(NotificationEntityType).nullable(),
	entityId: z.number().nullable(),
	category: z.nativeEnum(NotificationCategory).nullable(),
	priority: z.nativeEnum(NotificationPriority),
	isRead: z.boolean(),
	readAt: z.string().nullable(),
	metadata: z.record(z.string(), z.unknown()).nullable(),
	userId: z.number(),
	companyId: z.number().nullable(),
	listId: z.number().nullable(),
	status: z.nativeEnum(NotificationStatus),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type Notification = z.infer<typeof NotificationSchema>;

// ── Paginated response ────────────────────────────────────────────────

export const NotificationResponseSchema = z.object({
	data: z.array(NotificationSchema),
	meta: MetaDataSchema,
});

export type NotificationResponse = z.infer<typeof NotificationResponseSchema>;

// ── Unread count ──────────────────────────────────────────────────────

export const UnreadCountSchema = z.object({
	unreadCount: z.number(),
});

export type UnreadCount = z.infer<typeof UnreadCountSchema>;

// ── Notification Preference ───────────────────────────────────────────

export const NotificationPreferenceSchema = z.object({
	id: z.number(),
	userId: z.number(),
	companyId: z.number().nullable(),
	eventType: z.string(),
	appEnabled: z.boolean(),
	emailEnabled: z.boolean(),
	digestEnabled: z.boolean(),
	frequency: z.nativeEnum(NotificationFrequency),
	thresholds: z.record(z.string(), z.unknown()).nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

export const NotificationPreferencesResponseSchema = z.array(NotificationPreferenceSchema);

export type NotificationPreferencesResponse = z.infer<typeof NotificationPreferencesResponseSchema>;

// ── Upsert preference request ─────────────────────────────────────────

export const UpsertNotificationPreferenceSchema = z.object({
	groupKey: z.nativeEnum(NotificationPreferenceGroup),
	appEnabled: z.boolean().optional(),
	emailEnabled: z.boolean().optional(),
	digestEnabled: z.boolean().optional(),
	frequency: z.nativeEnum(NotificationFrequency).optional(),
	thresholds: z.record(z.string(), z.unknown()).nullable().optional(),
});

export type UpsertNotificationPreference = z.infer<typeof UpsertNotificationPreferenceSchema>;

// ── Notification group definition (from /notification-preferences/available) ──

export const NotificationSubToggleDefinitionSchema = z.object({
	key: z.string(),
	label: z.string(),
	description: z.string(),
	default: z.boolean(),
});

export type NotificationSubToggleDefinition = z.infer<typeof NotificationSubToggleDefinitionSchema>;

export const ReplyColleagueScopeSchema = z.enum(["everyone", "selected"]);

export type ReplyColleagueScope = z.infer<typeof ReplyColleagueScopeSchema>;

/**
 * Whose replies a group's "…to colleagues" sub-toggles cover (PREC-4259). `scopeKey` and `idsKey`
 * name the `thresholds` fields it is stored in; it matters only while one of `appliesTo` is on.
 */
export const NotificationColleagueScopeDefinitionSchema = z.object({
	scopeKey: z.string(),
	idsKey: z.string(),
	label: z.string(),
	description: z.string(),
	appliesTo: z.array(z.string()),
	default: ReplyColleagueScopeSchema,
	options: z.array(z.object({ value: ReplyColleagueScopeSchema, label: z.string() })),
});

export type NotificationColleagueScopeDefinition = z.infer<
	typeof NotificationColleagueScopeDefinitionSchema
>;

export const NotificationGroupDefinitionSchema = z.object({
	key: z.nativeEnum(NotificationPreferenceGroup),
	label: z.string(),
	description: z.string(),
	eventTypes: z.array(z.nativeEnum(UserNotificationContentType)),
	subToggles: z.array(NotificationSubToggleDefinitionSchema).optional(),
	colleagueScope: NotificationColleagueScopeDefinitionSchema.optional(),
});

export type NotificationGroupDefinition = z.infer<typeof NotificationGroupDefinitionSchema>;

export const NotificationGroupDefinitionsResponseSchema = z.array(
	NotificationGroupDefinitionSchema
);

export type NotificationGroupDefinitionsResponse = z.infer<
	typeof NotificationGroupDefinitionsResponseSchema
>;

// ── Query params for listing notifications ────────────────────────────

export const NotificationQueryParamsSchema = z.object({
	page: z.number().optional(),
	limit: z.number().optional(),
	isRead: z.enum(["true", "false"]).optional(),
	category: z.nativeEnum(NotificationCategory).optional(),
	priority: z.nativeEnum(NotificationPriority).optional(),
});

export type NotificationQueryParams = z.infer<typeof NotificationQueryParamsSchema>;
